import { useState } from "react";
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Compass, 
  Route, 
  Layers,
  Eye,
  Plus,
  Settings
} from "lucide-react";
import { TrailsMap } from "./components/TrailsMap";
import { LiveTracking } from "./components/LiveTracking";
import { WildlifeSightings } from "./components/WildlifeSightings";
import { MapSettings } from "./components/MapSettings";

export function MappingDashboard() {
  const [activeView, setActiveView] = useState("trails");
  const { t } = useTranslation('business');

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">{t('mappingNavigation')}</h1>
          <p className="text-gray-500 mt-2">
            {t('mappingSubtitle')}
          </p>
        </div>
        <div className="flex gap-4">
          <Button variant="outline" className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            {t('changeMapLayer')}
          </Button>
          <Button className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            {t('newTrail')}
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              {t('activeTrails')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">12</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              {t('activeTours')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">4</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              {t('wildlifeSightingsToday')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">23</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              {t('activeGuides')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">8</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card className="bg-white">
        <CardContent className="p-6">
          <Tabs value={activeView} onValueChange={setActiveView}>
            <TabsList className="bg-slate-50/80 p-1 rounded-lg shadow-sm border border-slate-200/60 h-12 mb-4">
              <TabsTrigger 
                value="trails"
                className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
              >
                <Route className="w-4 h-4" />
                {t('trailsSanctuary')}
              </TabsTrigger>
              <TabsTrigger 
                value="tracking"
                className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
              >
                <Compass className="w-4 h-4" />
                {t('liveTracking')}
              </TabsTrigger>
              <TabsTrigger 
                value="wildlife"
                className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
              >
                <Eye className="w-4 h-4" />
                {t('wildlifeSightings')}
              </TabsTrigger>
              <TabsTrigger 
                value="settings"
                className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
              >
                <Settings className="w-4 h-4" />
                {t('mapSettings')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="trails">
              <TrailsMap />
            </TabsContent>

            <TabsContent value="tracking">
              <LiveTracking />
            </TabsContent>

            <TabsContent value="wildlife">
              <WildlifeSightings />
            </TabsContent>

            <TabsContent value="settings">
              <MapSettings />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}